import * as React from 'react';

import {
  onAuthStateChanged,
  type User as FirebaseUser,
  type AuthProvider,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { flushSync } from 'react-dom';
import { getAuthDeferred } from '@/utils/firebase';
import { getUserUsersAuthGet } from '@/api';
import { useNavigate } from '@tanstack/react-router';
import { User } from '@/api/generated/types.gen';
import { getPostHog } from '@/lib/posthog';

const auth = getAuthDeferred();

export type AuthContextType = {
  isAuthenticated: boolean;
  isInitialLoading: boolean;
  emailVerified: boolean;
  googleUser: any;
  login: (provider: AuthProvider) => Promise<void>;
  logout: () => Promise<void>;
  user: User | null;
  setUser: (user: User | null) => void;
  getUser: (token: string) => Promise<User | undefined>;
};

const AuthContext = React.createContext<AuthContextType | null>(null);

export function AuthContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // Starts empty: auth.currentUser is a Firebase user, not our User, and made pages redirect as if signed in
  const [user, setUser] = React.useState<User | null>(null);
  const [isInitialLoading, setIsInitialLoading] = React.useState(true);
  const isAuthenticated = !!user;
  const [googleUser, setGoogleUser] = React.useState<any>(null);
  const [isGoogleProvider, setIsGoogleProvider] = React.useState(false);
  const navigate = useNavigate();
  function isGoogleAccount(user: FirebaseUser): boolean {
    return user.providerData.some(
      (provider: AuthProvider) => provider.providerId === 'google.com',
    );
  }

  function handleGoogleAccount(user: FirebaseUser) {
    if (isGoogleAccount(user)) {
      setGoogleUser({
        profile_image: user.photoURL || '',
        uid: user.uid,
        email: user.email || '',
        first_name: user.displayName?.split(' ')[0] || '',
        last_name: user.displayName?.split(' ')[1] || '',
      });
      const urlSearchParams = new URLSearchParams(window.location.search);
      const search = urlSearchParams.get('token');
      navigate({
        to: '/register',
        search: search ? { token: search } : undefined,
      });
    }
  }

  const getUser = async (token: string) => {
    const { data } = await getUserUsersAuthGet({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return data;
  };

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      flushSync(async () => {
        try {
          if (user) {
            setIsGoogleProvider(isGoogleAccount(user));
          }
          const token = await user?.getIdToken();
          if (token) {
            const userDetails = await getUser(token);
            if (userDetails) {
              setUser(userDetails);
              getPostHog()?.identify(userDetails.id, {
                email: userDetails.email,
                name: userDetails.first_name
                  ? `${userDetails.first_name} ${userDetails.last_name ?? ''}`.trim()
                  : undefined,
              });
            } else {
              handleGoogleAccount(user);
            }
          }
        } catch (error) { }

        setIsInitialLoading(false);
      });
    });
    return () => unsubscribe();
  }, []);

  const logout = React.useCallback(async () => {
    await signOut(auth);
    getPostHog()?.reset();
    setUser(null);
    setGoogleUser(null);
    setIsInitialLoading(false);
  }, []);

  const login = React.useCallback(async (provider: AuthProvider) => {
    const result = await signInWithPopup(auth, provider);
    flushSync(() => {
      setUser(result.user);
      setIsInitialLoading(false);
    });
  }, []);


  const emailVerified = isGoogleProvider || (user?.email_verified ?? false);

  return (
    <AuthContext.Provider
      value={{
        isInitialLoading,
        isAuthenticated,
        emailVerified,
        user,
        login,
        logout,
        setUser,
        getUser,
        googleUser: googleUser,
      }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Helper function to get current token
export async function getCurrentToken(): Promise<string | null> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    return null;
  }
  return await currentUser.getIdToken();
}
